'use client';
import React from 'react';

const steps = [
  {
    number: '01',
    title: 'Submit Intake Form',
    description:
      'Complete the secure client intake form with your business details, service needs, and any existing financial documents. All data is handled confidentially.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="22" height="22" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 10h12M8 14h12M8 18h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Scope & Estimate',
    description:
      'I review your submission and provide a detailed scope of work with a transparent, itemized estimate. No hidden fees, no surprises.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
        <path d="M14 4v20M4 14h20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="14" cy="14" r="10" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Engagement & Execution',
    description:
      'Once agreed, I begin work following a structured methodology. You receive regular progress updates and can ask questions at any point.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
        <path d="M6 14l5 5 11-11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="14" cy="14" r="10" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    number: '04',
    title: 'Delivery & Review',
    description:
      'Completed deliverables are shared securely. A review round is included to address any questions or adjustments before final sign-off.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
        <path d="M5 14l4 4 10-10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 7h22M3 21h22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

export default function ProcessSection() {
  return (
    <section id="process" className="bg-brand-grey50 py-16 sm:py-24 lg:py-32">
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="mb-10 sm:mb-16">
          <p
            className="text-primary font-sans text-xs font-semibold tracking-widest uppercase mb-4"
            style={{ letterSpacing: '3px' }}
          >
            How it works
          </p>
          <h2
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(26px, 6vw, 48px)',
              fontWeight: 700,
              lineHeight: '1.15',
              letterSpacing: '-1px',
              color: '#111111',
            }}
          >
            A clear, structured
            <br />
            process every time.
          </h2>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
          {steps?.map((step, index) => (
            <div
              key={step?.number}
              className="group relative bg-white p-6 sm:p-8 transition-all duration-500 hover:-translate-y-1"
              style={{
                transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
                borderRight: index % 2 === 0 && index < steps?.length - 1 ? '1px solid #eeeff1' : 'none',
                borderBottom: index < steps?.length - 1 ? '1px solid #eeeff1' : 'none',
                boxShadow: 'rgba(10, 10, 11, 0.04) 0px 4px 20px -4px',
              }}
            >
              {/* Step number */}
              <div
                className="text-brand-grey200 font-bold mb-5 sm:mb-6 transition-colors duration-300 group-hover:text-primary/20"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 'clamp(40px, 8vw, 56px)',
                  lineHeight: '1',
                  letterSpacing: '-2px',
                }}
              >
                {step?.number}
              </div>

              {/* Icon */}
              <div className="text-brand-grey500 mb-4 sm:mb-5 transition-colors duration-300 group-hover:text-primary">
                {step?.icon}
              </div>

              {/* Title */}
              <h4
                className="text-brand-dark font-semibold mb-2 sm:mb-3"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: '18px',
                  lineHeight: '1.3',
                }}
              >
                {step?.title}
              </h4>

              {/* Description */}
              <p
                className="text-brand-grey700 text-sm leading-relaxed"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.7' }}
              >
                {step?.description}
              </p>

              {/* Bottom accent */}
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-500 group-hover:w-full" style={{ transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
